import { useEffect, useState } from "react";
import { Users2, Flag, Activity } from "lucide-react";
import api from "@/lib/api";
import { TRACK_EMPTY, TRACK_NAV } from "./trackNav";
import TrackEmpty from "./TrackEmpty";
import TrackAthletes from "./TrackAthletes";
import TrackAthleteProfile from "./TrackAthleteProfile";
import TrackMeets from "./TrackMeets";
import TrackRankings from "./TrackRankings";
import TrackReports from "./TrackReports";
import TrackPerformance from "./TrackPerformance";
import TrackGroups from "./TrackGroups";
import TrackTesting from "./TrackTesting";

function TrackDashboardOverview({ orgId }) {
  const [stats, setStats] = useState(null);
  useEffect(() => {
    if (!orgId) return;
    Promise.all([api.get(`/track/athletes?organization_id=${orgId}`), api.get(`/track/meets?organization_id=${orgId}`)])
      .then(([a, m]) => setStats({ athletes: a.data.length, male: a.data.filter((x) => x.gender === "M").length, female: a.data.filter((x) => x.gender === "F").length, meets: m.data.length }))
      .catch(() => setStats({ athletes: 0, male: 0, female: 0, meets: 0 }));
  }, [orgId]);
  const Card = ({ icon: Icon, label, value, testid }) => (
    <div className="bg-[#0A0A0A] p-5 flex items-center gap-4" data-testid={testid}><Icon size={20} className="text-white/50" /><div><p className="font-display font-black text-3xl leading-none">{value}</p><p className="text-white/40 text-[10px] uppercase tracking-widest mt-1">{label}</p></div></div>
  );
  return (
    <div data-testid="track-dashboard-overview">
      <h1 className="font-display font-black uppercase tracking-tighter text-4xl md:text-5xl mb-6">Command Center</h1>
      {!stats ? <div className="border border-white/10 bg-[#121212] p-8 text-white/40 text-sm">Loading overview…</div> : (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 border border-white/10">
          <Card icon={Users2} label="Athletes" value={stats.athletes} testid="stat-athletes" />
          <Card icon={Users2} label="Male / Female" value={`${stats.male}/${stats.female}`} testid="stat-gender" />
          <Card icon={Flag} label="Meets" value={stats.meets} testid="stat-meets" />
          <Card icon={Activity} label="Season" value={<span className="text-lg">Active</span>} testid="stat-season" />
        </div>
      )}
      <p className="text-white/40 text-sm mt-6">Use the navigation to manage rosters, enter meet results, view performance charts, rankings and export reports.</p>
    </div>
  );
}

export default function TrackModule({ section, orgId, canWrite }) {
  const [athlete, setAthlete] = useState(null);
  if (!orgId) return <div className="border border-white/10 bg-[#121212] p-8 text-white/40 text-sm" data-testid="track-no-org-selected">Select an organization to manage its Track OS data.</div>;

  if (athlete && (section === "male_roster" || section === "female_roster"))
    return <TrackAthleteProfile athlete={athlete} orgId={orgId} canWrite={canWrite} onBack={() => setAthlete(null)} />;

  switch (section) {
    case "dashboard": return <TrackDashboardOverview orgId={orgId} />;
    case "male_roster": return <TrackAthletes orgId={orgId} gender="M" canWrite={canWrite} onOpenAthlete={setAthlete} />;
    case "female_roster": return <TrackAthletes orgId={orgId} gender="F" canWrite={canWrite} onOpenAthlete={setAthlete} />;
    case "groups": return <TrackGroups orgId={orgId} canWrite={canWrite} />;
    case "testing": return <TrackTesting orgId={orgId} canWrite={canWrite} />;
    case "meets":
    case "results": return <TrackMeets orgId={orgId} canWrite={canWrite} />;
    case "performance": return <TrackPerformance orgId={orgId} />;
    case "rankings": return <TrackRankings orgId={orgId} />;
    case "reports": return <TrackReports orgId={orgId} canWrite={canWrite} />;
    default:
      const meta = TRACK_EMPTY[section] || { title: TRACK_NAV.find((n) => n.key === section)?.label, msg: "This section is coming soon." };
      return <TrackEmpty testid={`track-empty-${section}`} title={meta.title} message={meta.msg} />;
  }
}
